// Public intake form page (v5) — mobile-responsive, branded, unauthenticated.
// Route: /intake/:firmSlug/:formSlug
//
// Renders the fields for a specific published intake form.  Submits back
// to /api/public/intake/:firmSlug/:formSlug.  Honeypot field included so
// bots that auto-fill get silently 200'd but never saved.

import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

const API_BASE = process.env.REACT_APP_API_URL || ''

export default function PublicIntake () {
  const { firmSlug, formSlug } = useParams()
  const [form, setForm] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [values, setValues] = useState({})
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submissionError, setSubmissionError] = useState('')
  const [currentPage, setCurrentPage] = useState(0)   // Gap #7 — multi-page navigation
  // Gap #6 — consultation scheduling (MVP): slots fetched when scheduling is
  // enabled, user picks one, and we book it right after the intake POST succeeds.
  const [availableSlots, setAvailableSlots] = useState([])
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [bookingResult, setBookingResult] = useState(null)
  // Declared with the other submit state, ABOVE both its setter (in the
  // submit handler) and its reader (the SuccessScreen render).
  const [submitResult, setSubmitResult] = useState(null)

  useEffect(() => {
    document.title = 'Contact Us — Intake Form'
    fetch(`${API_BASE}/api/public/intake/${encodeURIComponent(firmSlug)}/${encodeURIComponent(formSlug)}`)
      .then(async (r) => {
        if (!r.ok) throw new Error(r.status === 404 ? 'Form not found or unpublished.' : `HTTP ${r.status}`)
        return r.json()
      })
      .then((f) => {
        setForm(f)
        document.title = `${f.name} — ${f.firm_name || 'Contact Us'}`
        // Capture UTM params + referrer for attribution
        const params = new URLSearchParams(window.location.search)
        setValues({
          utm_source: params.get('utm_source') || '',
          utm_medium: params.get('utm_medium') || '',
          utm_campaign: params.get('utm_campaign') || '',
          utm_content: params.get('utm_content') || '',
          referrer_url: document.referrer || '',
        })
        // Gap #5 — inject GA4 gtag if the firm configured one.  The measurement
        // ID has already been format-validated server-side (G-XXXXXXXXXX).
        if (f.ga4_measurement_id && !window.gtag) {
          const s1 = document.createElement('script')
          s1.async = true
          s1.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(f.ga4_measurement_id)}`
          document.head.appendChild(s1)
          const s2 = document.createElement('script')
          s2.text = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${f.ga4_measurement_id}');`
          document.head.appendChild(s2)
        }
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }, [firmSlug, formSlug])

  // Gap #6 — fetch slots when scheduling is enabled.  Runs once form loads.
  useEffect(() => {
    if (!form?.consult_scheduling_enabled) return
    fetch(`${API_BASE}/api/public/consult/${encodeURIComponent(firmSlug)}/slots?limit=12`)
      .then(r => r.json())
      .then(d => setAvailableSlots(d.slots || []))
      .catch(() => setAvailableSlots([]))
  }, [form?.consult_scheduling_enabled, firmSlug])

  const setField = (key, value) => setValues((v) => ({ ...v, [key]: value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmissionError('')
    // Client-side required check
    for (const f of form.fields || []) {
      if (f.is_required && !values[f.field_key]) {
        setSubmissionError(`Please fill in "${f.label}"`)
        return
      }
    }
    if (form.require_terms_agreement && !termsAccepted) {
      setSubmissionError('Please agree to the terms before submitting.')
      return
    }
    setSubmitting(true)
    const payload = { ...values }
    if (termsAccepted) payload._terms_accepted = true
    try {
      const r = await fetch(`${API_BASE}/api/public/intake/${encodeURIComponent(firmSlug)}/${encodeURIComponent(formSlug)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const data = await r.json().catch(() => ({}))
      if (!r.ok) {
        setSubmissionError(data.error || `Submission failed (HTTP ${r.status})`)
        setSubmitting(false)
        return
      }
      // GA4 conversion event on successful submission
      if (typeof window.gtag === 'function' && form.ga4_measurement_id) {
        try {
          window.gtag('event', 'generate_lead', {
            form_slug: form.slug,
            practice_area: form.practice_area || 'unspecified',
            source: values.utm_source || 'direct'
          })
        } catch { /* GA errors are not our problem */ }
      }
      // Gap #6 — book the picked slot right after the intake POST succeeds
      if (form.consult_scheduling_enabled && selectedSlot) {
        try {
          const bookRes = await fetch(`${API_BASE}/api/public/consult/${encodeURIComponent(firmSlug)}/book`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              slot_start_utc: selectedSlot.start_utc,
              lead_email: values.email || '',
              lead_name: [values.first_name, values.last_name].filter(Boolean).join(' ').trim(),
              lead_id: data.lead_id || null,
              phone: values.phone || null,
              notes: values.matter_description || null
            })
          })
          const bookData = await bookRes.json().catch(() => ({}))
          if (bookRes.ok) setBookingResult(bookData)
          // If booking fails (409 slot-taken, etc.) still show success — the
          // lead is captured; admin can manually schedule.
        } catch { /* ignore — non-blocking */ }
      }
      if (data.redirect_url) {
        window.location.href = data.redirect_url
        return
      }
      setSubmitResult(data)
      setSubmitted(true)
    } catch (err) {
      setSubmissionError('Network error — please try again or call the office directly.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <div className="max-w-2xl mx-auto p-8 text-center text-gray-500">Loading form…</div>
  if (error) return <div className="max-w-2xl mx-auto p-8"><div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-6">⚠️ {error}</div></div>
  if (submitted) return <SuccessScreen form={form} bookingResult={bookingResult} result={submitResult} />

  // Gap #7 — evaluate a field's visibility_rule against the current values.
  // Returns true if the field should show.  Undefined/null rule = always show.
  const evalVisibility = (rule, vals) => {
    if (!rule || typeof rule !== 'object' || !rule.field_key) return true
    const v = vals[rule.field_key]
    switch (rule.op) {
      case 'eq':     return String(v ?? '') === String(rule.value ?? '')
      case 'neq':    return String(v ?? '') !== String(rule.value ?? '')
      case 'in':     return Array.isArray(rule.value) && rule.value.some(x => String(x) === String(v ?? ''))
      case 'nin':    return Array.isArray(rule.value) && !rule.value.some(x => String(x) === String(v ?? ''))
      case 'truthy': return !!v && v !== 'false' && v !== '0'
      case 'falsy':  return !v || v === 'false' || v === '0'
      default:       return true
    }
  }

  // Gap #7 — multi-page support.  Group by page_number first, then by section
  // for visual grouping within a page.  All fields on ALL pages are always
  // available to the visibility evaluator (a rule on page 2 can reference a
  // value entered on page 1).
  const visibleFields = form.fields.filter(f => evalVisibility(f.visibility_rule, values))
  const pageNumbers = Array.from(new Set(visibleFields.map(f => f.page_number || 1))).sort((a, b) => a - b)
  const totalPages = pageNumbers.length || 1
  const currentPageIdx = Math.min(currentPage, totalPages - 1)
  const currentPageNum = pageNumbers[currentPageIdx] || 1

  // Fields on the current page only, grouped by section
  const sections = {}
  for (const f of visibleFields.filter(f => (f.page_number || 1) === currentPageNum)) {
    const key = f.section || 'Form'
    if (!sections[key]) sections[key] = []
    sections[key].push(f)
  }

  // Firm branding (Gap #4) — accent color, logo, banner, footer come from the
  // form GET response; fall back to sensible neutral defaults if unset.
  const accent = form.firm_accent_color || '#2563eb'
  const accentStyle = { backgroundColor: accent }
  // Slight tint of accent for the subtitle text (10% lighter via alpha) — CSS
  // rgba works fine on the hex-encoded accent by using it as a background.
  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        {form.firm_banner_html && (
          <div className="mb-4 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: form.firm_banner_html }} />
        )}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <header style={accentStyle} className="text-white p-6 flex items-center gap-4">
            {form.firm_logo_url && (
              <img src={form.firm_logo_url} alt={`${form.firm_name} logo`}
                className="h-14 w-14 rounded-lg bg-white/95 p-1 object-contain flex-shrink-0"
                onError={(e) => { e.currentTarget.style.display = 'none' }} />
            )}
            <div className="min-w-0">
              <h1 className="text-2xl font-bold break-words">{form.firm_name}</h1>
              <p className="text-white/80 text-sm mt-1">{form.name}</p>
            </div>
          </header>

          {form.intro_html && (
            <div className="prose prose-sm max-w-none p-6 border-b border-gray-100" dangerouslySetInnerHTML={{ __html: form.intro_html }} />
          )}

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* HONEYPOT — hidden field bots auto-fill; humans never see it */}
            <input type="text" name="website_url" tabIndex={-1} autoComplete="off"
              value={values.website_url || ''}
              onChange={(e) => setField('website_url', e.target.value)}
              style={{ position: 'absolute', left: '-9999px', top: '-9999px', opacity: 0 }}
              aria-hidden="true"
            />

            {totalPages > 1 && (
              <div className="flex items-center justify-between border-b border-gray-100 pb-2 mb-4 text-xs text-gray-600">
                <span>Step {currentPageIdx + 1} of {totalPages}</span>
                <div className="flex gap-1">
                  {pageNumbers.map((p, i) => (
                    <span key={p} className={`h-1.5 w-8 rounded-full ${i === currentPageIdx ? '' : 'bg-gray-200'}`}
                      style={i === currentPageIdx ? { backgroundColor: accent } : {}} />
                  ))}
                </div>
              </div>
            )}

            {Object.entries(sections).map(([sectionName, fields]) => (
              <fieldset key={sectionName} className="space-y-4">
                <legend className="text-sm font-semibold text-gray-700 uppercase tracking-wider">{sectionName}</legend>
                {fields.map((f) => <FieldRow key={f.field_key} field={f} value={values[f.field_key]} onChange={(v) => setField(f.field_key, v)} />)}
              </fieldset>
            ))}

            {/* Consult scheduling picker (Gap #6) — only on last page */}
            {currentPageIdx === totalPages - 1 && form.consult_scheduling_enabled && availableSlots.length > 0 && (
              <div className="border border-gray-200 rounded-lg p-4 space-y-2">
                <div className="font-medium text-sm">📅 Book your consultation (optional)</div>
                <p className="text-xs text-gray-500">Pick a time that works for you.  We'll email a calendar invitation right after you submit.</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {availableSlots.slice(0, 12).map(s => {
                    const isSelected = selectedSlot?.start_utc === s.start_utc
                    const label = new Date(s.start_utc).toLocaleString('en-US', {
                      timeZone: s.display_tz, weekday: 'short', month: 'short', day: 'numeric',
                      hour: 'numeric', minute: '2-digit'
                    })
                    return (
                      <button key={s.start_utc} type="button"
                        onClick={() => setSelectedSlot(isSelected ? null : s)}
                        style={isSelected ? { ...accentStyle, color: '#fff', borderColor: accent } : {}}
                        className={`text-xs rounded-lg border px-3 py-2 transition-colors ${isSelected ? '' : 'border-gray-200 hover:border-gray-400 bg-white text-gray-700'}`}>
                        {label}
                      </button>
                    )
                  })}
                </div>
                {selectedSlot && (
                  <button type="button" onClick={() => setSelectedSlot(null)} className="text-[10px] text-gray-500 hover:underline">Clear selection</button>
                )}
              </div>
            )}
            {currentPageIdx === totalPages - 1 && form.consult_scheduling_enabled && availableSlots.length === 0 && (
              <div className="border border-gray-200 rounded-lg p-3 text-xs text-gray-600 bg-gray-50">
                📅 No consultation slots are available online right now.  Submit your inquiry and someone will reach out to schedule.
              </div>
            )}

            {/* Terms + submit only on the LAST page */}
            {currentPageIdx === totalPages - 1 && form.require_terms_agreement && form.terms_html && (
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="prose prose-sm max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: form.terms_html }} />
                <label className="flex items-start gap-2 mt-3 text-sm text-gray-700">
                  <input type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="mt-0.5" required />
                  <span>I have read and agree to the above terms.</span>
                </label>
              </div>
            )}

            {submissionError && (
              <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm">
                {submissionError}
              </div>
            )}

            {totalPages > 1 && currentPageIdx > 0 && (
              <button type="button" onClick={() => setCurrentPage(currentPageIdx - 1)}
                className="w-full mb-2 border border-gray-300 text-gray-700 font-medium py-3 rounded-lg hover:bg-gray-50 transition-colors">
                ← Back
              </button>
            )}

            {totalPages > 1 && currentPageIdx < totalPages - 1 ? (
              <button type="button" onClick={() => { setCurrentPage(currentPageIdx + 1); window.scrollTo({ top: 0, behavior: 'smooth' }) }}
                style={accentStyle}
                className="w-full text-white font-medium py-3 rounded-lg transition-colors hover:brightness-110">
                Next →
              </button>
            ) : (
            <button type="submit" disabled={submitting}
              style={accentStyle}
              className="w-full disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-3 rounded-lg transition-colors hover:brightness-110">
              {submitting ? 'Submitting…' : (form.submit_button_label || 'Submit')}
            </button>
            )}
            <p className="text-xs text-gray-400 text-center">
              Your submission is secure and confidential.  This form does not create an attorney-client relationship.
            </p>
          </form>
        </div>

        {form.firm_footer_html && (
          <div className="mt-4 prose prose-sm max-w-none text-center text-gray-600" dangerouslySetInnerHTML={{ __html: form.firm_footer_html }} />
        )}
        <div className="mt-2 text-xs text-gray-400 text-center">
          {form.firm_website ? <a href={form.firm_website} target="_blank" rel="noopener noreferrer" className="hover:underline">{new URL(form.firm_website).host}</a> : null}
          {form.firm_website && (form.firm_phone || form.firm_email) ? ' · ' : ''}
          {form.firm_phone ? <a href={`tel:${form.firm_phone}`} className="hover:underline">{form.firm_phone}</a> : null}
          {form.firm_phone && form.firm_email ? ' · ' : ''}
          {form.firm_email ? <a href={`mailto:${form.firm_email}`} className="hover:underline">{form.firm_email}</a> : null}
        </div>
      </div>
    </div>
  )
}

function FieldRow ({ field, value, onChange }) {
  const commonInput = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500'
  const label = (
    <label htmlFor={`f_${field.field_key}`} className="block text-sm font-medium text-gray-700 mb-1">
      {field.label}
      {field.is_required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
  )
  const help = field.help_text ? <p className="text-xs text-gray-500 mt-1">{field.help_text}</p> : null

  if (field.field_type === 'heading') return <h3 className="text-lg font-semibold text-gray-800 pt-2">{field.label}</h3>

  if (field.field_type === 'textarea') return (
    <div>{label}
      <textarea id={`f_${field.field_key}`} rows={5} required={field.is_required}
        value={value || ''} onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder || ''} className={commonInput} />{help}
    </div>
  )

  if (field.field_type === 'select') return (
    <div>{label}
      <select id={`f_${field.field_key}`} required={field.is_required}
        value={value || ''} onChange={(e) => onChange(e.target.value)} className={commonInput}>
        <option value="">— Select —</option>
        {(field.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>{help}
    </div>
  )

  if (field.field_type === 'radio') return (
    <div>{label}
      <div className="space-y-1">
        {(field.options || []).map((o) => (
          <label key={o.value} className="flex items-center gap-2 text-sm">
            <input type="radio" name={field.field_key} value={o.value}
              checked={value === o.value} onChange={(e) => onChange(e.target.value)} required={field.is_required} />
            <span>{o.label}</span>
          </label>
        ))}
      </div>{help}
    </div>
  )

  if (field.field_type === 'checkbox') return (
    <div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} required={field.is_required} />
        <span>{field.label}{field.is_required && <span className="text-red-500 ml-0.5">*</span>}</span>
      </label>{help}
    </div>
  )

  if (field.field_type === 'file') return (
    <div>{label}
      <p className="text-xs text-gray-500 mb-2">File upload not enabled in this preview.  Please describe your matter in text; we'll request documents after review.</p>
      {help}
    </div>
  )

  // text, email, phone, date, url, number all render as <input>
  const inputType = { email: 'email', phone: 'tel', date: 'date', url: 'url', number: 'number' }[field.field_type] || 'text'
  return (
    <div>{label}
      <input id={`f_${field.field_key}`} type={inputType} required={field.is_required}
        value={value || ''} onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder || ''} className={commonInput} />{help}
    </div>
  )
}

function SuccessScreen ({ form, bookingResult, result }) {
  const accent = form.firm_accent_color || '#2563eb'
  // The server admits an attachment only after a clean virus scan, and refuses
  // it otherwise.  Saying nothing here would leave the sender believing the
  // firm holds a document it does not — the failure the admission gate exists
  // to avoid.  (The embeddable widget already surfaces this via data.message;
  // this is the same truth on the hosted page.)
  const refused = Array.isArray(result?.rejected_files) ? result.rejected_files : []
  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="text-5xl mb-4" style={{ color: accent }}>✓</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Thank you!</h2>
          <p className="text-gray-600">We've received your inquiry.  One of our attorneys will review the details and be in touch shortly.</p>
          {refused.length > 0 && (
            <div className="mt-6 text-left bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="font-semibold text-amber-900 mb-2">
                {refused.length === 1 ? 'One attachment could not be accepted' : `${refused.length} attachments could not be accepted`}
              </p>
              <ul className="text-sm text-amber-800 space-y-1">
                {refused.map((f, i) => (
                  <li key={i}><span className="font-mono">{f.filename}</span> — {f.reason}</li>
                ))}
              </ul>
              <p className="text-sm text-amber-800 mt-3">
                Your inquiry itself was received.  Please call the office to send {refused.length === 1 ? 'this document' : 'these documents'} another way.
              </p>
            </div>
          )}
          {bookingResult?.display_slot && (
            <div className="mt-4 border border-emerald-200 bg-emerald-50 rounded-lg p-3 text-sm text-emerald-900">
              📅 Your consultation is confirmed for<br /><strong>{bookingResult.display_slot}</strong><br />
              <span className="text-xs">A calendar invitation has been sent to your email.</span>
            </div>
          )}
          <div className="mt-6 flex items-center justify-center gap-2">
            {form.firm_logo_url && (
              <img src={form.firm_logo_url} alt="" className="h-8 w-8 object-contain"
                onError={(e) => { e.currentTarget.style.display = 'none' }} />
            )}
            <p className="text-xs text-gray-500">{form.firm_name}</p>
          </div>
        </div>
        {form.firm_footer_html && (
          <div className="mt-4 prose prose-sm max-w-none text-center text-gray-600" dangerouslySetInnerHTML={{ __html: form.firm_footer_html }} />
        )}
      </div>
    </div>
  )
}
